import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { User } from "@shared/models/auth";

export const AUTH_REDIRECT_COUNT_KEY = "auth_redirect_attempts";
export const AUTH_REDIRECT_FIRST_AT_KEY = "auth_redirect_first_at";

export function clearAuthRedirectCounter() {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_FIRST_AT_KEY);
  } catch {
    // ignore storage errors
  }
}

async function fetchUser(): Promise<User | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  const user = (await response.json()) as User;
  clearAuthRedirectCounter();
  return user;
}

async function logout(): Promise<void> {
  window.location.href = "/api/logout";
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
